import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Container,
  useMediaQuery,
  useScrollTrigger,
  Slide,
  Tooltip,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
// Тема управляется через ThemeToggle компонент
import ThemeToggle from '../common/ThemeToggle';

// Иконки
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import CodeIcon from '@mui/icons-material/Code';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import MailIcon from '@mui/icons-material/Mail';
import TranslateIcon from '@mui/icons-material/Translate';

// Пользовательское событие для изменения языка
export const LANGUAGE_CHANGE_EVENT = 'portfolio-language-change';

function HideOnScroll(props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Header = (props) => {
  const theme = useTheme();
  const location = useLocation();
  // Фикс для Material-UI 5.x
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Состояние для языка (по умолчанию русский)
  const [language, setLanguage] = useState(() => {
    const savedLang = localStorage.getItem('portfolioLanguage');
    return savedLang || 'ru';
  });

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Используемся для отслеживания активного пункта меню
  const [activeItem, setActiveItem] = useState('/');

  useEffect(() => {
    setActiveItem(location.pathname);
  }, [location.pathname]);

  // Определяем, находимся ли мы в режиме редактирования
  const isEditMode = location.pathname.startsWith('/edit');

  // Функция для создания правильных ссылок на основе текущего режима
  const getRoutePath = (path) => {
    if (isEditMode) {
      // Если находимся в режиме редактирования, добавляем /edit перед всеми путями
      return `/edit${path === '/' ? '' : path}`;
    }
    return path;
  };

  // Переключение языка
  const toggleLanguage = () => {
    const newLang = language === 'ru' ? 'en' : 'ru';
    setLanguage(newLang);
    localStorage.setItem('portfolioLanguage', newLang);

    // Инициирование пользовательского события для обновления языка во всем приложении
    const event = new CustomEvent(LANGUAGE_CHANGE_EVENT, { detail: { language: newLang } });
    window.dispatchEvent(event);

    // Обновление страницы для применения языка ко всему контенту
    // window.location.reload(); // Раскомментируйте эту строку, если нужно полное обновление страницы
  };

  const menuItems = [
    { text: language === 'ru' ? 'Главная' : 'Home', path: getRoutePath('/'), icon: <HomeIcon /> },
    { text: language === 'ru' ? 'Навыки' : 'Skills', path: getRoutePath('/skills'), icon: <CodeIcon /> },
    { text: language === 'ru' ? 'Проекты' : 'Projects', path: getRoutePath('/projects'), icon: <WorkIcon /> },
    { text: language === 'ru' ? 'Опыт' : 'Experience', path: getRoutePath('/experience'), icon: <SchoolIcon /> },
    { text: language === 'ru' ? 'Контакты' : 'Contact', path: getRoutePath('/contact'), icon: <MailIcon /> },
  ];

  // Функция для проверки активного пункта меню
  const isActive = (path) => {
    return activeItem === path;
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const drawer = (
    <Box
      sx={{ width: 280 }}
      role="presentation"
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <Typography
          variant="h6"
          component={RouterLink}
          to="/"
          sx={{
            textDecoration: 'none',
            color: 'text.primary',
            fontWeight: 'bold',
            mb: 1
          }}
          onClick={handleDrawerToggle}
        >
          Никита Никоноров
        </Typography>
        <Box sx={{ display: 'flex', mt: 1 }}>
          <ThemeToggle />
          <Tooltip title={language === 'ru' ? 'Switch to English' : 'Переключить на русский'}>
            <IconButton
              color="inherit"
              onClick={toggleLanguage}
              aria-label="Переключить язык"
              sx={{ ml: 1 }}
            >
              <TranslateIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Divider sx={{ mb: 1 }} />

      <List>
        {menuItems.map((item, index) => (
          <ListItem
            button
            component={RouterLink}
            to={item.path}
            key={index}
            onClick={handleDrawerToggle}
            aria-current={item.path === location.pathname ? 'page' : undefined}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(92, 107, 192, 0.08)',
              },
              '&:focus': {
                outline: '2px solid',
                outlineColor: 'rgba(92, 107, 192, 0.5)',
                outlineOffset: '2px',
              },
              backgroundColor: isActive(item.path) ? 'rgba(92, 107, 192, 0.1)' : 'transparent',
              borderRadius: '0 20px 20px 0',
              ml: 1,
              mb: 0.5,
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: item.path === location.pathname ? 'bold' : 'normal',
              }}
            />
          </ListItem>
        ))}
      </List>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {language === 'ru' ? 'Версия 3.5.0' : 'Version 3.5.0'}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <HideOnScroll {...props}>
        <AppBar
          position="fixed"
          elevation={scrolled ? 4 : 2}
          sx={{
            // solid opaque header background
            backgroundColor: theme.palette.mode === 'dark' ? '#303F9F' : '#3F51B5',
            color: '#ffffff',
            transition: 'box-shadow 0.4s ease',
            boxShadow: scrolled
              ? '0px 4px 20px rgba(0, 0, 0, 0.4)'
              : '0px 2px 10px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Container maxWidth="lg">
            <Toolbar sx={{ py: 0.5 }}>
              <Typography
                variant="h6"
                component={RouterLink}
                to="/"
                sx={{
                  flexGrow: 1,
                  fontWeight: 'bold',
                  color: 'inherit',
                  textDecoration: 'none',
                  letterSpacing: '1px',
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    color: theme.palette.mode === 'dark'
                      ? theme.palette.primary.light
                      : 'rgba(255,255,255,0.9)',
                  },
                  transition: 'color 0.3s ease',
                }}
              >
                {language === 'ru' ? 'Никита Никоноров' : 'Nikita Nikonorov'}
              </Typography>

              {/* Кнопки языка и темы для всех разрешений */}
              <Box sx={{ display: 'flex', ml: 1, mr: isMobile ? 1 : 0 }}>
                <ThemeToggle />
                <Tooltip title={language === 'ru' ? 'Switch to English' : 'Переключить на русский'}>
                  <IconButton
                    color="inherit"
                    onClick={toggleLanguage}
                    aria-label="Переключить язык"
                    sx={{ ml: 0.5 }}
                  >
                    <TranslateIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {isMobile ? (
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
              ) : (
                <Box sx={{ display: 'flex', ml: 2 }}>
                  {menuItems.map((item) => (
                    <Button
                      key={item.text}
                      component={RouterLink}
                      to={item.path}
                      color="inherit"
                      aria-current={item.path === location.pathname ? 'page' : undefined}
                      startIcon={item.icon}
                      sx={{
                        mx: 0.5,
                        px: 1.5,
                        py: 1,
                        fontWeight: item.path === location.pathname ? 'bold' : 'normal',
                        position: 'relative',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        '&:after': {
                          content: '""',
                          position: 'absolute',
                          width: item.path === location.pathname ? '100%' : '0',
                          height: '3px',
                          bottom: '0',
                          left: '0',
                          backgroundColor: theme.palette.primary.main,
                          transition: 'width 0.3s ease',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(92, 107, 192, 0.08)',
                        },
                        '&:hover:after': {
                          width: '100%',
                        },
                        '&:focus': {
                          outline: '2px solid',
                          outlineColor: 'rgba(92, 107, 192, 0.5)',
                          outlineOffset: '2px',
                        },
                      }}
                    >
                      {item.text}
                    </Button>
                  ))}
                </Box>
              )}
            </Toolbar>
          </Container>
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={handleDrawerToggle}
          >
            {drawer}
          </Drawer>
        </AppBar>
      </HideOnScroll>
      {/* Placeholder to prevent content jump when header hides/shows */}
      <Toolbar sx={{ visibility: 'hidden' }} />
    </>
  );
};

export default Header;
